// Per-administrator "unseen" state for the sidebar badges.
//
// Read status lives in the database (admin_unseen_acks), keyed to the signed-in
// administrator, so it survives refreshes, sign-outs and other devices, and one
// admin's acknowledgment never clears another admin's badge.
//
// NOTHING in this file resolves, approves, pays, completes or dismisses an
// operational record. It only writes read markers.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  baselineAckables,
  computeUnseen,
  countUnseenByBadge,
  loadUnseenUniverse,
  UNSEEN_BADGES,
  type UnseenBadgeKey,
  type UnseenItem,
} from "@/lib/adminUnseen";

interface UnseenContextValue {
  loading: boolean;
  /** Unseen count per badge (0 = hide the bubble). */
  counts: Partial<Record<UnseenBadgeKey, number>>;
  /** Item keys this admin has not seen yet, per badge. */
  unseenKeys: (badge: UnseenBadgeKey) => Set<string>;
  isUnseen: (key: string) => boolean;
  /** Mark specific items read — used for rows actually displayed on screen. */
  markSeen: (keys: string[]) => Promise<void>;
  /** Mark every unseen item in one section read. */
  markAllSeen: (badge: UnseenBadgeKey) => Promise<void>;
  refresh: () => Promise<void>;
}

const noop = async () => {};

const AdminUnseenContext = createContext<UnseenContextValue>({
  loading: true,
  counts: {},
  unseenKeys: () => new Set(),
  isUnseen: () => false,
  markSeen: noop,
  markAllSeen: noop,
  refresh: noop,
});

export const useAdminUnseen = () => useContext(AdminUnseenContext);

export function AdminUnseenProvider({ children }: { children: ReactNode }) {
  const { user, isAdmin } = useAuth();
  const userId = isAdmin ? user?.id : undefined;

  const [items, setItems] = useState<UnseenItem[]>([]);
  const [acks, setAcks] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const baselineRunning = useRef(false);

  const writeAcks = useCallback(
    async (rows: UnseenItem[]) => {
      if (!userId || rows.length === 0) return;
      const payload = rows.map((i) => ({
        user_id: userId,
        badge_key: i.badge,
        item_key: i.key,
        acked_revision: i.revision,
        acked_at: new Date().toISOString(),
      }));
      const { error } = await (supabase.from("admin_unseen_acks" as any) as any).upsert(payload, {
        onConflict: "user_id,item_key",
      });
      if (error) {
        console.warn("[admin unseen] could not save read markers", error);
        return;
      }
      setAcks((prev) => {
        const next = new Map(prev);
        for (const i of rows) next.set(i.key, i.revision);
        return next;
      });
    },
    [userId],
  );

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    const [universe, ackRes, baseRes] = await Promise.all([
      loadUnseenUniverse(),
      (supabase.from("admin_unseen_acks" as any) as any)
        .select("item_key, acked_revision")
        .eq("user_id", userId),
      (supabase.from("admin_unseen_baselines" as any) as any)
        .select("badge_key")
        .eq("user_id", userId),
    ]);

    const ackMap = new Map<string, string>();
    for (const r of (ackRes.data ?? []) as any[]) ackMap.set(r.item_key, r.acked_revision);

    // Launch baseline — the first time this admin loads a section, everything
    // already sitting there is treated as already seen, EXCEPT records that
    // carry their own genuine unread flag. Without this, day one would show a
    // wall of red bubbles for historical items.
    const seenBadges = new Set<string>(((baseRes.data ?? []) as any[]).map((r) => r.badge_key));
    const missing = UNSEEN_BADGES.filter((b) => !seenBadges.has(b));
    if (missing.length && !baselineRunning.current) {
      baselineRunning.current = true;
      const toAck = baselineAckables(universe.filter((i) => missing.includes(i.badge)));
      if (toAck.length) {
        const payload = toAck.map((i) => ({
          user_id: userId,
          badge_key: i.badge,
          item_key: i.key,
          acked_revision: i.revision,
        }));
        await (supabase.from("admin_unseen_acks" as any) as any).upsert(payload, {
          onConflict: "user_id,item_key",
        });
        for (const i of toAck) ackMap.set(i.key, i.revision);
      }
      await (supabase.from("admin_unseen_baselines" as any) as any).upsert(
        missing.map((b) => ({ user_id: userId, badge_key: b })),
        { onConflict: "user_id,badge_key" },
      );
      baselineRunning.current = false;
    }

    setItems(universe);
    setAcks(ackMap);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Keep counts honest while the admin works. A refresh re-reads the same
  // records; unchanged records keep the same revision, so nothing turns unread
  // just because data was re-fetched.
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`admin-unseen-${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "enrollment_min_checks" }, () => void load())
      .on("postgres_changes", { event: "*", schema: "public", table: "class_interest_alerts" }, () => void load())
      .on("postgres_changes", { event: "*", schema: "public", table: "instructor_proposals" }, () => void load())
      .on("postgres_changes", { event: "*", schema: "public", table: "open_house_registrations" }, () => void load())
      .on("postgres_changes", { event: "*", schema: "public", table: "payroll_periods" }, () => void load())
      .on("postgres_changes", { event: "*", schema: "public", table: "waiting_items" }, () => void load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [userId, load]);

  const counts = useMemo(() => countUnseenByBadge(items, acks), [items, acks]);

  const byKey = useMemo(() => {
    const m = new Map<string, UnseenItem>();
    for (const i of items) m.set(i.key, i);
    return m;
  }, [items]);

  const unseenSet = useMemo(
    () => new Set(computeUnseen(items, acks).map((i) => i.key)),
    [items, acks],
  );

  const value: UnseenContextValue = {
    loading,
    counts,
    unseenKeys: (badge) =>
      new Set(computeUnseen(items, acks).filter((i) => i.badge === badge).map((i) => i.key)),
    isUnseen: (key) => unseenSet.has(key),
    markSeen: async (keys) => {
      const rows = keys
        .map((k) => byKey.get(k))
        .filter((i): i is UnseenItem => !!i && unseenSet.has(i.key));
      await writeAcks(rows);
    },
    markAllSeen: async (badge) => {
      await writeAcks(computeUnseen(items, acks).filter((i) => i.badge === badge));
    },
    refresh: load,
  };

  return <AdminUnseenContext.Provider value={value}>{children}</AdminUnseenContext.Provider>;
}

/**
 * Marks the items a page has ACTUALLY rendered as seen.
 *
 * Callers pass the keys that are on screen right now — after filters, tabs,
 * pagination and collapsed groups — so hidden items stay unread.
 */
export function useMarkSeenWhenDisplayed(keys: string[], ready: boolean = true) {
  const { markSeen } = useAdminUnseen();
  const serialized = keys.join("|");
  useEffect(() => {
    if (!ready || keys.length === 0) return;
    const t = window.setTimeout(() => { void markSeen(keys); }, 1200);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, ready]);
}
